import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { FileText, ArrowLeft, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

export function SignupPage() {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    full_name: '',
    company: '',
  });
  const [loading, setLoading] = useState(false);
  const { signup } = useAuth();
  const navigate = useNavigate();

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.email || !formData.password || !formData.full_name) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }
    
    if (formData.password !== formData.confirmPassword) {
      toast.error('Les mots de passe ne correspondent pas');
      return;
    }
    
    if (formData.password.length < 6) {
      toast.error('Le mot de passe doit contenir au moins 6 caractères');
      return;
    }
    
    setLoading(true);
    try {
      await signup({
        email: formData.email,
        password: formData.password,
        full_name: formData.full_name,
        company: formData.company || null,
      });
      toast.success('Compte créé avec succès');
      navigate('/dashboard');
    } catch (error) {
      const message = error.response?.data?.detail || 'Erreur lors de la création du compte';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex">
      {/* Left side - Image */}
      <div className="hidden lg:block lg:w-1/2 relative">
        <div className="absolute inset-0 bg-primary/90 z-10" />
        <img
          src="https://images.unsplash.com/photo-1712697236921-f02a449dd9e5?w=1200&q=80"
          alt="Architectural blueprints"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 z-20 flex items-center justify-center p-12">
          <div className="text-white text-center">
            <h2 className="font-heading text-3xl font-bold mb-4">
              Rejoignez DCE Analyzer
            </h2>
            <p className="text-white/80 text-lg max-w-md">
              Des centaines d'entreprises du BTP nous font confiance pour analyser leurs appels d'offres.
            </p>
          </div>
        </div>
      </div>

      {/* Right side - Form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-8"
            data-testid="back-to-home"
          >
            <ArrowLeft size={16} />
            Retour à l'accueil
          </Link>

          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 bg-primary rounded-xl flex items-center justify-center">
              <FileText className="w-7 h-7 text-white" />
            </div>
            <div>
              <h1 className="font-heading text-2xl font-bold text-slate-900">Créer un compte</h1>
              <p className="text-slate-500 text-sm">Commencez à analyser vos DCE</p>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="full_name">Nom complet *</Label>
              <Input
                id="full_name"
                name="full_name"
                type="text"
                placeholder="Jean Dupont"
                value={formData.full_name}
                onChange={handleChange}
                data-testid="signup-fullname"
                className="h-12"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">Adresse email *</Label>
              <Input
                id="email"
                name="email"
                type="email"
                placeholder="votre@email.com"
                value={formData.email}
                onChange={handleChange}
                data-testid="signup-email"
                className="h-12"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="company">Entreprise (optionnel)</Label>
              <Input
                id="company"
                name="company"
                type="text"
                placeholder="Nom de votre entreprise"
                value={formData.company}
                onChange={handleChange}
                data-testid="signup-company"
                className="h-12"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Mot de passe *</Label>
              <Input
                id="password"
                name="password"
                type="password"
                placeholder="Minimum 6 caractères"
                value={formData.password}
                onChange={handleChange}
                data-testid="signup-password"
                className="h-12"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Confirmer le mot de passe *</Label>
              <Input
                id="confirmPassword"
                name="confirmPassword"
                type="password"
                placeholder="Confirmez votre mot de passe"
                value={formData.confirmPassword}
                onChange={handleChange}
                data-testid="signup-confirm-password"
                className="h-12"
              />
            </div>

            <Button
              type="submit"
              className="w-full h-12 mt-6"
              disabled={loading}
              data-testid="signup-submit"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Création...
                </>
              ) : (
                'Créer mon compte'
              )}
            </Button>
          </form>

          <p className="mt-6 text-center text-slate-600">
            Déjà un compte ?{' '}
            <Link to="/login" className="text-primary font-medium hover:underline" data-testid="goto-login">
              Se connecter
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
